/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventMessageUtils,
  InteractionRequiredAuthError,
  InteractionStatus,
  IPublicClientApplication,
  SilentRequest,
} from "@azure/msal-browser";
import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { error } from "../../../util/log";
import { selectCurrentAccountClaims, setAccountIdToken } from "../storage";

/** The user closed the interactive window before it completed. */
const USER_CANCELLED = "user_cancelled";

/** MSAL gave up watching the hidden iframe/popup it opened to renew silently. */
const MONITOR_WINDOW_TIMEOUT = "monitor_window_timeout";

export interface UseAcquireTokenOptions {
  instance: IPublicClientApplication;
  inProgress: InteractionStatus;
  accounts: AccountInfo[];
  createTokenRequest: (prompt?: string) => SilentRequest;
  createCELinkTokenRequest: (prompt: string, accountNumber: string) => SilentRequest;
  handleMsalLogout: () => Promise<void>;
  resetMsalInstance: () => void;
  onTokenAcquired?: (result: AuthenticationResult) => void;
}

const getErrorCode = (err: unknown): string | undefined =>
  typeof err === "object" && err !== null && "errorCode" in err
    ? String((err as { errorCode: unknown }).errorCode)
    : undefined;

/**
 * Resolves once MSAL reports no interaction in progress.
 *
 * Interactive calls such as `acquireTokenRedirect` are rejected with `interaction_in_progress`
 * while another interaction is running, so they have to wait for the current one to settle.
 * Silent calls have no such restriction and must never wait.
 */
export const waitForInteractionToSettle = (
  instance: IPublicClientApplication,
  currentStatus: InteractionStatus
): Promise<void> => {
  if (currentStatus === InteractionStatus.None) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let status: InteractionStatus = currentStatus;
    let callbackId: string | null = null;

    callbackId = instance.addEventCallback((message: EventMessage) => {
      const nextStatus = EventMessageUtils.getInteractionStatusFromEvent(message, status);
      if (nextStatus === null) {
        return;
      }
      status = nextStatus;
      if (status === InteractionStatus.None) {
        if (callbackId) {
          instance.removeEventCallback(callbackId);
        }
        resolve();
      }
    });

    if (callbackId === null) {
      // The instance refused the subscription, so there is no settle event to wait for.
      resolve();
    }
  });
};

/**
 * Returns a stable callback that acquires an access token for the selected account.
 *
 * The callback is registered on the Axios request interceptor, so its identity is deliberately
 * kept stable: everything that changes on each render is read through a ref instead of a
 * dependency.
 *
 * The silent acquisition is never gated on the interaction status — gating it is what left the
 * app serving a `null` token for the window right after an account switch. Only the interactive
 * redirect fallback waits, because that is the call MSAL rejects while an interaction is pending.
 * Errors outside the three handled branches are rethrown rather than swallowed as a `null` token.
 *
 * Every successful acquisition writes MSAL's ID token back to Redux, which is otherwise left
 * holding the copy captured at login for as long as the tab lives. `setAccountIdToken` ignores an
 * unchanged token, so dispatching on every request costs nothing.
 */
export const useAcquireToken = ({
  instance,
  inProgress,
  accounts,
  createTokenRequest,
  createCELinkTokenRequest,
  handleMsalLogout,
  resetMsalInstance,
  onTokenAcquired,
}: UseAcquireTokenOptions) => {
  const accountNumber = useSelector(selectCurrentAccountClaims)?.account_number;
  const dispatch = useDispatch();

  const latest = useRef({
    inProgress,
    createTokenRequest,
    createCELinkTokenRequest,
    handleMsalLogout,
    resetMsalInstance,
    onTokenAcquired,
  });
  latest.current = {
    inProgress,
    createTokenRequest,
    createCELinkTokenRequest,
    handleMsalLogout,
    resetMsalInstance,
    onTokenAcquired,
  };

  return useCallback(async (): Promise<AuthenticationResult | null> => {
    if (accounts.length === 0) {
      return null;
    }

    const request = accountNumber
      ? latest.current.createCELinkTokenRequest("login", accountNumber)
      : latest.current.createTokenRequest("login");

    try {
      const result = await instance.acquireTokenSilent(request);
      if (result.account) {
        dispatch(
          setAccountIdToken({
            homeAccountId: result.account.homeAccountId,
            idToken: result.idToken,
          })
        );
      }
      latest.current.onTokenAcquired?.(result);
      return result;
    } catch (err) {
      error("Error acquiring token silently:", err);

      if (err instanceof InteractionRequiredAuthError) {
        await waitForInteractionToSettle(instance, latest.current.inProgress);
        try {
          await instance.acquireTokenRedirect(request);
        } catch {
          await latest.current.handleMsalLogout();
        }
        return null;
      }

      const errorCode = getErrorCode(err);
      if (errorCode === USER_CANCELLED) {
        latest.current.resetMsalInstance();
        return null;
      }
      if (errorCode === MONITOR_WINDOW_TIMEOUT) {
        await latest.current.handleMsalLogout();
        return null;
      }

      throw err;
    }
  }, [dispatch, instance, accounts, accountNumber]);
};
